import React, { useState } from "react";
import { toast } from "sonner";
import { Copy, Smartphone } from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui-kit";
import { usePrefs } from "@/contexts/PrefsContext";

const ua = typeof navigator !== "undefined" ? navigator.userAgent : "";
const isIOS = /iPad|iPhone|iPod/.test(ua) || (/Macintosh/.test(ua) && typeof document !== "undefined" && "ontouchend" in document);
const isAndroid = /Android/i.test(ua);

type Props = { lat?: number | null; lng?: number | null; name?: string; fallbackUrl?: string; className?: string; testId: string; children: React.ReactNode };

export const NavigateButton = ({ lat, lng, name = "", fallbackUrl, className, testId, children }: Props) => {
  const { t } = usePrefs();
  const [open, setOpen] = useState(false);
  const has = typeof lat === "number" && typeof lng === "number";
  if (!has && !fallbackUrl) return null;
  if (!has) return <a href={fallbackUrl} target="_blank" rel="noreferrer" data-testid={testId} className={className}>{children}</a>;

  const ll = `${lat},${lng}`;
  const apps = [
    { id: "google", label: "Google Maps", color: "bg-[#1a73e8]", href: `https://www.google.com/maps/dir/?api=1&destination=${ll}` },
    { id: "waze", label: "Waze", color: "bg-[#33ccff]", href: `https://waze.com/ul?ll=${ll}&navigate=yes` },
    ...(isIOS ? [{ id: "apple", label: "Apple Maps", color: "bg-neutral-800", href: `https://maps.apple.com/?daddr=${ll}&q=${encodeURIComponent(name)}` }] : []),
  ];
  const copy = async () => {
    try { await navigator.clipboard.writeText(ll); toast.success(t("nav.copied")); } catch { toast.error(ll); }
  };
  const item = "flex w-full items-center gap-3 rounded-2xl border bg-card p-3 font-bold hover:bg-accent transition-[background-color]";

  return (
    <>
      <button type="button" onClick={() => setOpen(true)} data-testid={testId} className={className}>{children}</button>
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent data-testid="nav-chooser" className="max-w-sm">
          <DialogHeader><DialogTitle>{t("nav.choose")}</DialogTitle></DialogHeader>
          {name && <p className="text-sm text-muted-foreground -mt-2" dir="auto">{name}</p>}
          <div className="space-y-2">
            {apps.map((a) => (
              <a key={a.id} href={a.href} target="_blank" rel="noreferrer" onClick={() => setOpen(false)} data-testid={`nav-app-${a.id}`} className={item}>
                <span className={`h-9 w-9 rounded-xl ${a.color} text-white grid place-items-center text-sm font-black`}>{a.label[0]}</span>{a.label}
              </a>
            ))}
            {isAndroid && (
              <a href={`geo:${ll}?q=${ll}(${encodeURIComponent(name)})`} onClick={() => setOpen(false)} data-testid="nav-app-other" className={item}>
                <span className="h-9 w-9 rounded-xl bg-emerald-600 text-white grid place-items-center"><Smartphone className="h-4 w-4" /></span>{t("nav.otherApps")}
              </a>
            )}
            <button type="button" onClick={copy} data-testid="nav-copy-coords" className={item}>
              <span className="h-9 w-9 rounded-xl bg-muted grid place-items-center"><Copy className="h-4 w-4" /></span>
              <span className="flex-1 text-start">{t("nav.copy")}<span className="block text-xs font-normal text-muted-foreground" dir="ltr">{ll}</span></span>
            </button>
          </div>
          <p className="text-xs text-muted-foreground">{t("nav.hint")}</p>
        </DialogContent>
      </Dialog>
    </>
  );
};
